import { ThemeEvents, VariantUpdateEvent, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays the product purchase card with dynamic price updates.
 * This component listens for variant update events and updates prices and availability.
 */
class ProductPurchaseCard extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.handleVariantUpdate.bind(this));

    // Listen for quantity changes to update the total partial price
    const quantitySelector = this.querySelector('quantity-selector-component, cart-quantity-selector-component');
    if (quantitySelector) {
      quantitySelector.addEventListener('change', this.handleQuantityChange.bind(this));
      
      // Initialize quantity input with current quantity value
      const quantityInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="quantity"]'));
      const selectorQuantity = /** @type {any} */ (quantitySelector);
      if (quantityInput && selectorQuantity.quantity !== undefined) {
        quantityInput.value = selectorQuantity.quantity.toString();
      }
    }

    // Intercept form submission for AJAX add to cart
    const form = this.querySelector('form[data-type="add-to-cart-form"]');
    if (form) {
      form.addEventListener('submit', this.handleFormSubmit.bind(this));
    }
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.handleVariantUpdate);

    // Remove quantity change listener
    const quantitySelector = this.querySelector('quantity-selector-component, cart-quantity-selector-component');
    if (quantitySelector) {
      quantitySelector.removeEventListener('change', this.handleQuantityChange);
    }

    // Remove form listener
    const form = this.querySelector('form[data-type="add-to-cart-form"]');
    if (form) {
      form.removeEventListener('submit', this.handleFormSubmit);
    }
  }

  /**
   * Handles the variant update event and updates prices
   * @param {CustomEvent} event - The variant update event
   */
  handleVariantUpdate = (event) => {
    const { detail } = event;

    // Make sure we have the variant data and it matches this product
    if (!detail || !detail.resource) return;

    // Check if this product card is for the same product as the one being updated
    // The event is dispatched from variant-picker with productId in detail.data
    if (detail.data?.productId && detail.data.productId !== this.dataset.productId) return;

    const variant = detail.resource;

    // Update the price elements
    this.updatePrices(variant);

    // Update the hidden input with the new variant ID
    this.updateVariantInput(variant);

    // Update the availability and button state
    this.updateAvailability(variant);
  };

  /**
   * Handles quantity input changes and updates the total partial price
   * @param {Event} event - The change event
   */
  handleQuantityChange = (event) => {
    // Update the hidden quantity input in the form
    const quantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component, cart-quantity-selector-component'));
    if (quantitySelector) {
      const quantityInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="quantity"]'));
      if (quantityInput && quantitySelector.quantity !== undefined) {
        quantityInput.value = quantitySelector.quantity.toString();
      }
    }

    // Get current price from the displayed price
    const priceSection = this.querySelector('.product-purchase-card__price-section, .product-purchase-card-mobile__price-section');
    if (!priceSection) return;

    const currentPriceEl = priceSection.querySelector('.price-current, .product-purchase-card__price-main, .product-purchase-card-mobile__price-main');
    if (!currentPriceEl) return;

    // Parse the price text - remove currency formatting
    const priceText = currentPriceEl.textContent || '0';
    const currentPrice = this.parsePrice(priceText);

    // Update total partial price
    this.updateTotalPartialPrice(currentPrice);
  };

  /**
   * Handles form submission and adds product to cart via AJAX
   * @param {Event} event - The form submit event
   */
  handleFormSubmit = (event) => {
    event.preventDefault();

    const form = /** @type {HTMLFormElement | null} */ (event.target);
    if (!form) return;

    const button = /** @type {HTMLButtonElement | null} */ (form.querySelector('button[type="submit"]'));
    if (!button) return;

    // Don't submit if button is already disabled
    if (button.disabled) return;

    // Disable the button during submission
    button.disabled = true;
    button.setAttribute('data-loading', 'true');
    const originalText = button.textContent;
    const buttonTextSpan = button.querySelector('.button-text');
    if (buttonTextSpan) {
      buttonTextSpan.textContent = 'Agregando...';
    } else {
      button.textContent = 'Agregando...';
    }

    // Get form data
    const formData = new FormData(form);

    // Get variant ID from form
    const variantId = /** @type {string} */ (formData.get('id'));
    
    // Get quantity from the quantity selector component (priority) or form data
    let quantity = 1;
    const quantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component, cart-quantity-selector-component'));
    if (quantitySelector && quantitySelector.getValue) {
      quantity = parseInt(quantitySelector.getValue()) || 1;
    } else {
      // Fallback to form data
      const quantityInput = /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="quantity"]'));
      if (quantityInput) {
        quantity = parseInt(quantityInput.value) || 1;
      }
    }
    
    const cartAddUrl = '/cart/add.js';

    fetch(cartAddUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: variantId,
        quantity: quantity,
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        // Show success message
        this.showNotification('Producto agregado al carrito', 'success');

        // Reset button text
        if (buttonTextSpan) {
          buttonTextSpan.textContent = originalText.includes('Agregando') ? 'Agregar al carrito' : originalText;
        } else {
          button.textContent = originalText;
        }
        button.disabled = false;
        button.removeAttribute('data-loading');

        // Dispatch cart update event for other components to listen to
        document.dispatchEvent(
          new CartUpdateEvent(data, form.id || '', {
            source: 'product-purchase-card',
            productId: this.dataset.productId,
            variantId: variantId,
          })
        );
      })
      .catch((error) => {
        console.error('Error adding to cart:', error);
        this.showNotification('Error al agregar el producto', 'error');

        // Reset button
        if (buttonTextSpan) {
          buttonTextSpan.textContent = originalText.includes('Agregando') ? 'Agregar al carrito' : originalText;
        } else {
          button.textContent = originalText;
        }
        button.disabled = false;
        button.removeAttribute('data-loading');
      });
  };

  /**
   * Shows a notification message
   * @param {string} message - The notification message
   * @param {'success' | 'error'} type - The notification type
   */
  showNotification(message, type = 'success') {
    // Create notification element
    const notification = document.createElement('div');
    notification.className = `product-purchase-card__notification product-purchase-card__notification--${type}`;
    notification.textContent = message;

    // Add to DOM
    document.body.appendChild(notification);

    // Show with animation
    requestAnimationFrame(() => {
      notification.classList.add('product-purchase-card__notification--visible');
    });

    // Auto-remove after 3 seconds
    setTimeout(() => {
      notification.classList.remove('product-purchase-card__notification--visible');
      setTimeout(() => {
        notification.remove();
      }, 300);
    }, 3000);
  }

  /**
   * Parses a formatted price string back to a number
   * @param {string} priceText - The formatted price string
   * @returns {number} - The parsed price value
   */
  parsePrice(priceText) {
    // Remove currency symbols and convert to number
    // Handle "$ 1,234.56" or "MXN 1,234.56" format
    const cleanPrice = priceText.replace(/[^\d,.]/g, '').replace(/,/g, '');
    return parseFloat(cleanPrice) || 0;
  }

  /**
   * Updates the price display elements
   * @param {{price: number, compare_at_price?: number}} variant - The variant object with price information
   */
  updatePrices(variant) {
    const priceSection = this.querySelector('.product-purchase-card__price-section, .product-purchase-card-mobile__price-section');
    if (!priceSection) return;

    const currentPrice = /** @type {number} */ (variant.price) / 100; // Convert from cents to dollars
    const comparePrice = variant.compare_at_price ? /** @type {number} */ (variant.compare_at_price) / 100 : null;

    // Update main price or both prices depending on if there's a compare price
    if (comparePrice && comparePrice > currentPrice) {
      // Has a sale price
      const currentPriceEl = priceSection.querySelector('.price-current');
      const comparePriceEl = priceSection.querySelector('.price-compare');

      if (currentPriceEl) {
        currentPriceEl.textContent = this.formatPrice(currentPrice);
      }
      if (comparePriceEl) {
        comparePriceEl.textContent = this.formatPrice(comparePrice);
      }

      // Update discount badge if it exists
      this.updateDiscountBadge(comparePrice, currentPrice);
    } else {
      // No sale price
      const mainPriceEl = priceSection.querySelector('.product-purchase-card__price-main, .product-purchase-card-mobile__price-main');
      if (mainPriceEl) {
        mainPriceEl.textContent = this.formatPrice(currentPrice);
      }

      // Remove discount badge if no sale
      const badge = this.querySelector('.product-purchase-card__badge, .product-purchase-card-mobile__badge');
      if (badge) {
        badge.remove();
      }
    }

    // Update total partial price
    this.updateTotalPartialPrice(currentPrice);
  }

  /**
   * Updates the discount badge with the new percentage
   * @param {number} comparePrice - The original price
   * @param {number} currentPrice - The sale price
   */
  updateDiscountBadge(comparePrice, currentPrice) {
    let badge = this.querySelector('.product-purchase-card__badge, .product-purchase-card-mobile__badge');
    const savings = comparePrice - currentPrice;
    const savingsPercent = Math.round((savings / comparePrice) * 100);

    if (badge) {
      badge.textContent = `-${savingsPercent}%`;
    } else {
      // Create badge if it doesn't exist
      const wrapper = this.querySelector('.product-purchase-card__wrapper, .product-purchase-card-mobile__wrapper');
      if (wrapper) {
        badge = document.createElement('div');
        badge.className = this.classList.contains('product-purchase-card-mobile') 
          ? 'product-purchase-card-mobile__badge' 
          : 'product-purchase-card__badge';
        badge.textContent = `-${savingsPercent}%`;
        wrapper.appendChild(badge);
      }
    }
  }

  /**
   * Updates the total partial price based on quantity
   * @param {number} price - The current price in dollars
   */
  updateTotalPartialPrice(price) {
    const totalPartialPriceEl = this.querySelector('.total-partial-price');
    if (!totalPartialPriceEl) return;

    // Get quantity from the quantity selector component or input
    let quantity = 1;
    const quantitySelector = /** @type {any} */ (
      this.querySelector('quantity-selector-component, cart-quantity-selector-component')
    );

    if (quantitySelector && quantitySelector.quantity !== undefined) {
      // If the component has a quantity property
      quantity = quantitySelector.quantity;
    } else {
      // Fall back to input search
      const quantityInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="quantity"]'));
      if (quantityInput) {
        quantity = parseInt(quantityInput.value) || 1;
      }
    }

    const total = price * quantity;
    totalPartialPriceEl.textContent = this.formatPrice(total);
  }

  /**
   * Updates the hidden variant input with the new variant ID
   * @param {{id: string | number}} variant - The variant object
   */
  updateVariantInput(variant) {
    const variantInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (variantInput && variant.id) {
      variantInput.value = /** @type {string} */ (variant.id);
    }
  }

  /**
   * Updates the availability status and button state
   * @param {{available: boolean}} variant - The variant object
   */
  updateAvailability(variant) {
    const addToCartButton = /** @type {HTMLButtonElement | null} */ (
      this.querySelector('.product-purchase-card__button[type="submit"], .product-purchase-card-mobile__button[type="submit"]')
    );
    if (!addToCartButton) return;

    const available = /** @type {boolean} */ (variant.available);

    if (available) {
      addToCartButton.disabled = false;
      const buttonText = addToCartButton.querySelector('.button-text');
      if (buttonText) {
        buttonText.textContent = 'Agregar al carrito';
      }
    } else {
      addToCartButton.disabled = true;
      const buttonText = addToCartButton.querySelector('.button-text');
      if (buttonText) {
        buttonText.textContent = 'Agotado';
      }
    }
  }

  /**
   * Formats a price value to currency string
   * @param {number} price - The price value in dollars
   * @returns {string} - The formatted price string
   */
  formatPrice(price) {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
    }).format(price);
  }
}

if (!customElements.get('product-purchase-card')) {
  customElements.define('product-purchase-card', ProductPurchaseCard);
}
